import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  VStack,
  Text,
  Spinner,
} from '@chakra-ui/react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { toast } from '../utils/toast';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }
    let cancelled = false;
    authAPI
      .verifyEmail(token)
      .then(() => {
        if (!cancelled) setStatus('success');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="gray.50"
      px={4}
    >
      <Container maxW="md">
        <Box bg="white" p={10} borderRadius="2xl" boxShadow="lg">
          <VStack gap={6} align="stretch" textAlign="center">
            {status === 'loading' && (
              <>
                <Spinner size="xl" color="purple.500" />
                <Text color="gray.600">Verifying...</Text>
              </>
            )}
            {status === 'success' && (
              <>
                <Heading size="lg" color="green.600">Email verified</Heading>
                <Text color="gray.600">
                  Your email has been verified successfully. You can log in now.
                </Text>
                <Button
                  colorScheme="purple"
                  onClick={() => navigate('/login')}
                  borderRadius="lg"
                >
                  Log in
                </Button>
              </>
            )}
            {status === 'error' && (
              <>
                <Heading size="lg" color="red.600">Link invalid or expired</Heading>
                <Text color="gray.600">
                  The verification link has expired (valid 24h) or is invalid. You can request a new link.
                </Text>
                <VStack gap={2}>
                  <Button
                    colorScheme="purple"
                    onClick={() => navigate('/resend-verification')}
                    borderRadius="lg"
                  >
                    Request new verification link
                  </Button>
                  <Button variant="ghost" onClick={() => navigate('/login')}>
                    Back to login
                  </Button>
                </VStack>
              </>
            )}
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default VerifyEmail;
